import { useInView } from 'react-intersection-observer';

import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';
import HeroWithBackground from 'Blocks/HeroWithBackground';
import { ProductsFeatured } from 'Blocks/ProductsFeatured/ProductsFeatured';

import type { srcOrNextImageProp } from 'PropTypes/imageProps';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';
import LoadingScene from 'Scenes/LoadingScene';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { useFetchProductsList } from 'dux/products/hooks';

import ProductsGrid from './ProductsGrid';

type BgPicture = {
  src: string;
  height: number;
  width: number;
  blurDataURL: string;
  blurWidth: number;
  blurHeight: number;
};

type Card = {
  displayingOrderOnPLP: number;
  label: string;
  price: number;
  bgPicture: BgPicture;
  bgLabelledPicture: BgPicture;
  bgPictureAlt: string;
  productPageUrl: string;
  currency: string;
  is_subscribable: boolean;
  slug: string;
  type: string;
  isCustom: boolean;
  hasMultiplePrices: boolean;
  dataClickOnCollection: string;
  regularPrice: number;
};

type ProductsBlockContent = {
  cards: Card[];
};

type ProductCollectionProps = {
  content: {
    hero: {
      color: string;
      headline: React.ReactNode;
      imageDesktop: typeof srcOrNextImageProp;
      imageMobile: typeof srcOrNextImageProp;
      imageTablet: typeof srcOrNextImageProp;
    };
    featured: Array<{
      ctaLabel: string;
      imgMobile: StaticImageData;
      imgDesktop: StaticImageData;
      title: string;
      url: string;
      dataClick?: string;
      dataFrom?: string;
    }>;
    seoBlurb: {
      headline?: string;
      subHead?: string;
    };
  };
  dataFrom?: string;
  productsBlockContent: ProductsBlockContent;
  productsToDisplayOnARow?: number[];
};

const ProductCollectiondWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProductsFeaturedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${legacyTheme.spacing.s24};
  align-items: center;
  justify-content: center;

  width: 100%;
  padding: 45px ${legacyTheme.spacing.s24};

  ${legacyTheme.breakpoints.up('sm')} {
    flex-direction: row;
    gap: ${legacyTheme.spacing.s24};
    padding: 45px 70px;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    gap: ${legacyTheme.spacing.s36};

    width: 90%;
    height: 327px;
    margin: 90px 0;
    padding: 45px 15px;
  }
`;

const StyledHeroWithBackground = styled(HeroWithBackground)`
  margin-top: 0;
  padding-top: 48px;

  h1 {
    margin-bottom: 0;
  }
  ${legacyTheme.breakpoints.up('md')} {
    margin-top: 144px;
    padding-top: 0;

    h1 {
      font-size: 57px;
    }
  }
`;

export const ProductCollection = ({
  content,
  dataFrom,
  productsBlockContent,
  productsToDisplayOnARow = [2, 3, 3],
}: ProductCollectionProps) => {
  const { isMobile } = useResponsiveVariant();
  const [refProductFeatured, inViewProductFeatured] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const { isLoaded } = useFetchProductsList();
  if (!isLoaded) {
    /* @ts-expect-error - LoadingScene has no TS types */
    return <LoadingScene />;
  }
  return (
    <BaseScene footer header>
      {/* @ts-expect-error LoadingScene not migrated */}
      <StyledHeroWithBackground
        backgroundPosition={isMobile ? 'center 60%' : 'center top'}
        content={content.hero}
        fixMinHeight
      />
      <ProductCollectiondWrapper>
        <ProductsGrid
          background="white"
          content={productsBlockContent}
          dataFrom={dataFrom}
          productsToDisplayOnARow={productsToDisplayOnARow}
        />
      </ProductCollectiondWrapper>
      <ProductsFeaturedWrapper ref={refProductFeatured}>
        {inViewProductFeatured &&
          content.featured.map((feature) => (
            <ProductsFeatured
              key={feature.title}
              ctaLabel={feature.ctaLabel}
              dataClick={feature.dataClick}
              dataFrom={feature.dataFrom}
              image={{
                alt: '',
                src: {
                  mobile: feature.imgMobile,
                  tablet: feature.imgDesktop,
                  desktop: feature.imgDesktop,
                },
              }}
              title={feature.title}
              url={feature.url}
            />
          ))}
      </ProductsFeaturedWrapper>
      {/* @ts-expect-error LoadingScene not migrated */}
      <BlockLayout backgroundColor="darkerBeige" color="noir" contentAlign="center">
        <Typography align={isMobile ? 'left' : 'center'} markupName="h1" paragraph variant="h2">
          {content.seoBlurb.headline}
        </Typography>
        <Typography align={isMobile ? 'left' : 'center'} color="grey" variant="p1">
          {content.seoBlurb.subHead}
        </Typography>
      </BlockLayout>
    </BaseScene>
  );
};
